import classNames from 'classnames';
import { scaleLinear } from 'd3';
import clamp from 'lodash/clamp';
import min from 'lodash/min';
import { type CSSProperties, useMemo } from 'react';

import { BAR_END_HOVER_SLOP } from '../../constants';
import { PatternDiagonalStripe } from '../../svg-patterns/patterns';
import {
  findNearestBandIndex,
  getDimmedPlotColor,
  getMaxTextWidth,
  getOtherColor,
  useChartTheme,
} from '../../utilities';
import { AxisLabel } from '../axis/axis-label';
import styles from './stacked-bar-chart.module.css';
import { buildGroupAxisLayout } from './stacked-bar-group-axis';
import {
  adjustSegmentSpans,
  findSegmentSpanAtCoord,
} from './stacked-bar-scale';
import {
  buildSegmentEntries,
  Dimensions,
  StackedBarLegend,
  type StackedBarRendererProps,
} from './stacked-bar-shared';
import {
  StackedBarSharedTooltip,
  type StackedBarTooltipState,
  useStackedBarTooltip,
} from './stacked-bar-tooltip';

const EMPTY_BAR_PATTERN_ID = 'diagonal-stripe-4';

export const HorizontalStackedBar = ({
  model,
  width,
  height,
  format,
  hideLegend,
  maxLabelWidth,
  formatLegendLabel,
  maxLegendLabelWidth,
  isTimeseries = false,
  highlightMatchingSegments = true,
  onSegmentClick,
}: StackedBarRendererProps) => {
  const {
    dataIndexed,
    displayLabels,
    segmentLabels,
    stackedSeries,
    emptyBars,
    colorScale,
    domainMax,
  } = model;

  const { theme } = useChartTheme();

  const paddingInner = Dimensions.NewBandPaddingInner;
  const paddingOuter = Dimensions.NewBandPaddingOuter;
  const segmentSpacing = Dimensions.NewSegmentSpacing;
  const legendHeight = hideLegend
    ? 0
    : Dimensions.LegendHeight + Dimensions.NewLegendTopMargin;
  const minHeight = dataIndexed.length * Dimensions.MinBarThickness;
  const maxHeight = dataIndexed.length * Dimensions.MaxBarThickness;
  const plotHeight = clamp(height - legendHeight, minHeight, maxHeight);

  const groupAxis = useMemo(
    () =>
      buildGroupAxisLayout({
        labels: dataIndexed.map((d) => d.label),
        isTimeseries,
        rangeLength: plotHeight,
        dimensions: { paddingInner, paddingOuter },
      }),
    [dataIndexed, isTimeseries, plotHeight]
  );
  const barHeight = groupAxis.barThickness;
  const rowSlop = Math.max(0, (groupAxis.step - barHeight) / 2);
  const focusHoveredOnly = !highlightMatchingSegments;

  const barStarts = useMemo(
    () => dataIndexed.map((_, index) => groupAxis.getBarStart(index)),
    [dataIndexed, groupAxis]
  );
  const emptyIndexSet = useMemo(
    () => new Set(emptyBars.map((bar) => bar._index)),
    [emptyBars]
  );

  const axisLabelTexts = groupAxis.isTime
    ? groupAxis.ticks.map((tick) => tick.label)
    : displayLabels;
  const labelWidth = min([
    getMaxTextWidth({
      texts: axisLabelTexts,
      fontSize: 11,
    }),
    maxLabelWidth,
  ]) as number;
  const plotWidth = Math.max(0, width - labelWidth - Dimensions.LabelPadding);
  const chartOffsetX = labelWidth + Dimensions.LabelPadding;

  const isPlotSizeValid = plotWidth > 0 && plotHeight > 0;
  const shouldHideLegend = hideLegend || legendHeight + plotHeight > height;

  const xScale = useMemo(
    () => scaleLinear().domain([0, domainMax]).range([0, plotWidth]),
    [domainMax, plotWidth]
  );

  const rowLayouts = useMemo(
    () =>
      dataIndexed.map((_, rowIndex) =>
        adjustSegmentSpans(
          stackedSeries.map((series) => {
            const segment = series[rowIndex];
            return segment
              ? Math.max(0, xScale(segment[1]) - xScale(segment[0]))
              : 0;
          }),
          segmentSpacing
        )
      ),
    [dataIndexed, stackedSeries, xScale]
  );

  const yAxisEntries = useMemo(
    () =>
      groupAxis.isTime
        ? groupAxis.ticks.map((tick, index) => ({
            key: `tick-${index}`,
            y: tick.coord,
            label: tick.label,
          }))
        : dataIndexed.map((d, index) => {
            const start = barStarts[index];
            return {
              key: `axis-${index}`,
              y: start === undefined ? undefined : start + barHeight / 2,
              label: displayLabels?.[index] ?? d.label,
            };
          }),
    [groupAxis, dataIndexed, displayLabels, barStarts, barHeight]
  );

  const hitTest = ([px, py]: [
    number,
    number,
  ]): StackedBarTooltipState | null => {
    const row = findNearestBandIndex(barStarts, barHeight, py, rowSlop);
    if (row === null) return null;

    if (emptyIndexSet.has(row)) {
      return {
        key: `empty:${row}`,
        title: dataIndexed[row]?.label ?? '',
        row,
        entries: undefined,
      };
    }

    const layouts = rowLayouts[row];
    if (layouts === undefined) return null;
    const hitIndex = findSegmentSpanAtCoord(layouts, px, BAR_END_HOVER_SLOP);
    if (hitIndex === null) return null;
    const hitSeries = stackedSeries[hitIndex];
    const hitSegment = hitSeries?.[row];
    if (hitSeries === undefined || hitSegment === undefined) return null;
    return {
      key: `${row}:${hitSeries.key}`,
      title: hitSeries.key,
      row,
      entries: buildSegmentEntries(
        hitSeries,
        hitSegment,
        format,
        focusHoveredOnly
      ),
    };
  };

  const { state, cursor, isVisible, gProps } = useStackedBarTooltip(hitTest);

  const highlightedSegment =
    isVisible && state?.entries !== undefined ? (state?.title ?? null) : null;
  // An empty-row hover ('') matches no series key, so everything dims.
  const hoveredState = isVisible ? state : null;
  const hoveredKey =
    hoveredState === null
      ? null
      : (hoveredState.entries !== undefined && hoveredState.title) || '';
  const hoveredRow = hoveredState === null ? null : hoveredState.row;

  const drillMode = onSegmentClick !== undefined;

  const barsContent = useMemo(
    () => (
      <>
        {stackedSeries.map((series, seriesIndex) => {
          const isMutedCategory =
            drillMode &&
            highlightedSegment !== null &&
            series.key !== highlightedSegment;
          const isDimmedSeries =
            !drillMode &&
            !focusHoveredOnly &&
            hoveredKey !== null &&
            series.key !== hoveredKey;
          const handleClick = drillMode
            ? () => onSegmentClick?.(series.key, colorScale(series.key))
            : undefined;
          return (
            <g
              key={`series-${series.key}`}
              fill={colorScale(series.key) ?? getOtherColor()}
              style={
                {
                  '--replots-dim-color': getDimmedPlotColor(
                    colorScale(series.key) ?? getOtherColor(),
                    theme
                  ),
                } as CSSProperties
              }
              className={classNames(
                styles.bar,
                isDimmedSeries && styles.seriesDimmed
              )}
            >
              {series.map((segment) => {
                const y = barStarts[segment.data._index];
                if (y === undefined) return null;
                const layout = rowLayouts[segment.data._index]?.[seriesIndex];
                if (layout === undefined || layout.span === 0) return null;
                const x = layout.start;
                const segmentWidth = Math.max(0, layout.span - segmentSpacing);
                if (segmentWidth === 0) return null;
                const isDimmedSegment =
                  !drillMode &&
                  focusHoveredOnly &&
                  hoveredKey !== null &&
                  !(
                    series.key === hoveredKey &&
                    segment.data._index === hoveredRow
                  );
                return (
                  <rect
                    key={`segment-${series.key}-${segment.data._index}`}
                    data-testid={`stacked-bar-segment-${series.key}-${segment.data._index}`}
                    x={x}
                    y={y}
                    fill={colorScale(series.key)}
                    width={segmentWidth}
                    height={barHeight}
                    className={classNames(
                      styles.segment,
                      drillMode && styles.segmentDrill,
                      isMutedCategory && styles.segmentMuted,
                      isDimmedSegment && styles.segmentDimmed
                    )}
                    rx={0}
                    cursor={drillMode ? 'pointer' : undefined}
                    onClick={handleClick}
                    onKeyDown={
                      handleClick
                        ? (e) => {
                            if (e.key === 'Enter' || e.key === ' ') {
                              e.preventDefault();
                              handleClick();
                            }
                          }
                        : undefined
                    }
                    role={drillMode ? 'button' : undefined}
                    tabIndex={drillMode ? 0 : undefined}
                  />
                );
              })}
            </g>
          );
        })}
        {emptyBars.map((emptyBar) => {
          const y = barStarts[emptyBar._index];
          if (y === undefined) return null;
          return (
            <rect
              key={`empty-bar-${emptyBar.label}-${emptyBar._index}`}
              x={0}
              y={y}
              width={plotWidth}
              height={barHeight}
              fill={`url(#${EMPTY_BAR_PATTERN_ID}) #cbd5e0`}
            />
          );
        })}
      </>
    ),
    [
      stackedSeries,
      emptyBars,
      barStarts,
      barHeight,
      rowLayouts,
      colorScale,
      plotWidth,
      drillMode,
      focusHoveredOnly,
      hoveredKey,
      hoveredRow,
      highlightedSegment,
      onSegmentClick,
      theme,
    ]
  );

  return (
    <div className={styles.horizontalStack}>
      <div className={styles.plotViewport}>
        <svg width="100%" height={plotHeight}>
          <defs>
            <PatternDiagonalStripe patternId={EMPTY_BAR_PATTERN_ID} />
          </defs>
          <g id="y-axis">
            {isPlotSizeValid &&
              yAxisEntries.map((entry) =>
                entry.y === undefined ? null : (
                  <AxisLabel
                    testId="stacked-horizontal-bar-chart-y-axis-label"
                    key={entry.key}
                    y={entry.y}
                    width={labelWidth}
                    availableHeight={groupAxis.isTime ? 16 : barHeight + 2}
                    label={entry.label}
                  />
                )
              )}
          </g>
          <g id="chart" transform={`translate(${chartOffsetX}, 0)`}>
            {isPlotSizeValid && (
              <g {...gProps}>
                {/* Full-plot hit surface: gaps hit-test like bars. */}
                <rect
                  x={0}
                  y={0}
                  width={plotWidth}
                  height={plotHeight}
                  fill="transparent"
                  cursor={drillMode ? 'pointer' : undefined}
                  onClick={
                    drillMode && highlightedSegment !== null
                      ? () =>
                          onSegmentClick?.(
                            highlightedSegment,
                            colorScale(highlightedSegment)
                          )
                      : undefined
                  }
                />
                {barsContent}
              </g>
            )}
          </g>
        </svg>
        <StackedBarSharedTooltip
          state={state}
          cursor={cursor}
          isVisible={isVisible}
        />
      </div>
      {!shouldHideLegend && isPlotSizeValid && (
        <StackedBarLegend
          className={styles.legendContainer}
          segmentLabels={segmentLabels}
          colorScale={colorScale}
          formatLegendLabel={formatLegendLabel}
          maxLegendLabelWidth={maxLegendLabelWidth}
          marginLeft={labelWidth + Dimensions.LabelPadding}
          highlightedLabel={highlightedSegment}
        />
      )}
    </div>
  );
};
